// The Google multi-metric trend — now a thin ADAPTER over the shared
// MultiTrendChart (src/modules/reporting/MultiTrendChart.tsx), which the Meta
// dashboard renders through too. The chart itself moved there rather than being
// copied: two chart stacks drift, and a chart drawing a client's numbers is the
// worst place for a drift nobody notices.
//
// What stays HERE is everything Google-specific, which is exactly why the shared
// component takes resolved series rather than a summary:
//
// ⚠️ Each series is recomputed per day from that day's ADDITIVE totals (never
// from a stored ratio), so a CTR line is the day's clicks ÷ that day's
// impressions rather than an average of averages.
//
// ⚠️ An unmeasured day plots as a GAP (null), never as 0. A zero impression
// share would draw a line to the floor and read as "we lost the whole auction
// that day" when in fact Google reported nothing.

import {
  computeMetrics,
  isMoneyMetric,
  metricDef,
  metricMoney,
  metricScalar,
} from '../../lib/googleAdsMetrics';
import { formatMetric } from './metricFormat';
import { convertGoogleMetrics } from './googleCurrency';
import type { GoogleMoneyDisplay } from './googleTrendSeries';
import { MultiTrendChart, type MultiTrendData } from '../reporting/MultiTrendChart';
import type {
  GoogleAdsMetricKey,
  GoogleAdsMetrics,
  GoogleAdsTrendPoint,
} from '../../types/googleAdsReporting';

/** Per-day metrics, rebuilt from that day's per-currency totals. */
function metricsForDay(point: GoogleAdsTrendPoint): GoogleAdsMetrics {
  return computeMetrics(new Map(point.byCurrency.map((c) => [c.currency, c.totals])));
}

/**
 * A plottable number for a metric on one day.
 *
 * Money is summed across currencies ONLY for the chart's shape — the KPI tiles
 * and tables keep money per-currency, and the tooltip shows the real per-currency
 * figure. With a single-currency book (the current one) they are identical; with
 * a mixed book the line is a shape, not a total, which is why the tooltip carries
 * the authoritative value.
 */
function plotValue(metrics: GoogleAdsMetrics, key: GoogleAdsMetricKey): number | null {
  if (isMoneyMetric(key)) {
    const amounts = metricMoney(metrics, key);
    return amounts.length > 0 ? amounts.reduce((n, a) => n + a.amount, 0) : null;
  }
  return metricScalar(metrics, key);
}

/** The Google summary's daily trend → the shared chart's series shape. PURE. */
export function buildGoogleMultiTrend(
  trend: GoogleAdsTrendPoint[] | undefined,
  selected: readonly GoogleAdsMetricKey[],
  money?: GoogleMoneyDisplay,
): MultiTrendData {
  const points = trend ?? [];
  // Per-day metrics computed ONCE and reused across every series, rather than
  // once per (series × day). ⚠️ Converted at the METRIC-BLOCK level so a money
  // ratio is re-derived from that day's converted total; Native returns the
  // block by identity, so nothing changes there.
  const perDay = points.map((p) => {
    const m = metricsForDay(p);
    return convertGoogleMetrics(m, money?.display ?? '', money?.snapshot) ?? m;
  });
  return {
    buckets: points.map((p) => p.date),
    // The Google trend is a daily series; the page's bucket grouping applies to
    // the single-metric chart and the tiles, not to this one.
    grouping: 'daily',
    series: selected.map((key) => ({
      key,
      label: metricDef(key)?.label ?? key,
      points: perDay.map((m) => ({
        value: plotValue(m, key),
        // The AUTHORITATIVE per-currency figure — never the summed proxy above.
        formatted: formatMetric(m, key),
      })),
    })),
  };
}

export function GoogleAdsTrend({
  trend,
  selected,
  loading,
  money,
}: {
  trend: GoogleAdsTrendPoint[] | undefined;
  selected: GoogleAdsMetricKey[];
  loading: boolean;
  /** The agency display currency. Absent = native. */
  money?: GoogleMoneyDisplay;
}): React.JSX.Element {
  return (
    <MultiTrendChart
      title="Trend"
      data={buildGoogleMultiTrend(trend, selected, money)}
      loading={loading}
    />
  );
}
